import { emitSdkEvent, emitSdkMetric, type SdkObservers } from './observers'
import { sdkConsole } from './logger'

export interface RemoteMcpClientTransport {
    url: string
    headers?: Record<string, string>
    fetch?: typeof fetch
}

export interface RemoteMcpHttpResponseObservation {
    url: string
    method: 'POST'
    requestKind: 'request' | 'notification'
    response: Response
}

export interface NormalizedMcpToolCallResult {
    content: unknown[]
    structuredContent: Record<string, unknown> | null
    errorText: string | null
}

export interface RemoteMcpRequestObservation {
    method: string
    params?: Record<string, unknown>
    connectionLabel: string
    connectionType: 'remote'
    callId: string
    capturedAt: string
    durationMs: number
    isError: boolean
    redactedParams: string | null
    redactedResult: string | null
    resultData: Record<string, unknown> | null
    toolResult: NormalizedMcpToolCallResult | null
}

export interface RemoteMcpClientHooks {
    onHttpResponse?(observation: RemoteMcpHttpResponseObservation): void | Promise<void>
    onRequestComplete?(observation: RemoteMcpRequestObservation): void | Promise<void>
    observers?: SdkObservers
}

interface JsonRpcRequest {
    jsonrpc: '2.0'
    id: string | number
    method: string
    params?: Record<string, unknown>
}

interface JsonRpcNotification {
    jsonrpc: '2.0'
    method: string
    params?: Record<string, unknown>
}

interface JsonRpcSuccess {
    jsonrpc: '2.0'
    id: string | number
    result: Record<string, unknown>
}

interface JsonRpcFailure {
    jsonrpc: '2.0'
    id: string | number | null
    error: {
        code: number
        message: string
        data?: unknown
    }
}

type JsonRpcMessage = JsonRpcSuccess | JsonRpcFailure

export interface McpRemoteTool {
    name: string
    title?: string
    description?: string
    inputSchema?: Record<string, unknown>
}

export interface McpRemoteToolCallResult {
    content?: unknown[]
    structuredContent?: Record<string, unknown> | null
    isError?: boolean
}

export const REMOTE_MCP_PROTOCOL_VERSION = '2025-06-18'

function parseStructuredContentBlock(block: unknown): Record<string, unknown> | null {
    if (!block || typeof block !== 'object') return null

    const candidate = block as { type?: unknown; text?: unknown }
    if (candidate.type !== 'text' || typeof candidate.text !== 'string') return null

    try {
        const parsed = JSON.parse(candidate.text)
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed as Record<string, unknown>
            : null
    } catch {
        return null
    }
}

function readTextBlock(block: unknown): string | null {
    if (!block || typeof block !== 'object') return null

    const candidate = block as { type?: unknown; text?: unknown }
    return candidate.type === 'text' && typeof candidate.text === 'string'
        ? candidate.text
        : null
}

export function normalizeMcpToolCallResult(result: Record<string, unknown>): NormalizedMcpToolCallResult {
    const rawContent = Array.isArray(result.content) ? [...result.content] : []

    let structuredContent = result.structuredContent && typeof result.structuredContent === 'object'
        ? result.structuredContent as Record<string, unknown>
        : null
    let derivedStructuredContentIndex = -1

    if (!structuredContent) {
        for (let index = 1; index < rawContent.length; index++) {
            const parsed = parseStructuredContentBlock(rawContent[index])
            if (parsed) {
                structuredContent = parsed
                derivedStructuredContentIndex = index
                break
            }
        }
    }

    const content = derivedStructuredContentIndex >= 0
        ? rawContent.filter((_block, index) => index !== derivedStructuredContentIndex)
        : rawContent

    let errorText: string | null = null
    if (result.isError === true) {
        errorText = typeof structuredContent?.error === 'string'
            ? structuredContent.error
            : content.map(readTextBlock).find((text): text is string => !!text) ?? 'MCP tool returned an error'
    }

    return { content, structuredContent, errorText }
}

export class RemoteMcpClient {
    private sessionId: string | null = null
    private nextId = 1
    private initializePromise: Promise<void> | null = null
    private readonly fetchRequest: typeof fetch
    private readonly url: string
    private readonly headers?: Record<string, string>

    constructor(
        private readonly serverName: string,
        transport: RemoteMcpClientTransport,
        private readonly hooks: RemoteMcpClientHooks = {},
    ) {
        this.url = transport.url
        this.headers = transport.headers
        const fetchImpl = transport.fetch ?? fetch
        this.fetchRequest = (input: RequestInfo | URL, init?: RequestInit) => fetchImpl.call(globalThis, input, init)
    }

    async listTools(): Promise<McpRemoteTool[]> {
        await this.ensureInitialized()

        const tools: McpRemoteTool[] = []
        let cursor: string | undefined
        do {
            const result = await this.sendRequest('tools/list', cursor ? { cursor } : undefined)
            const batch = Array.isArray(result.tools) ? result.tools as McpRemoteTool[] : []
            tools.push(...batch)
            cursor = typeof result.nextCursor === 'string' ? result.nextCursor : undefined
        } while (cursor)

        return tools
    }

    async callTool(name: string, args: Record<string, unknown>): Promise<McpRemoteToolCallResult> {
        await this.ensureInitialized()
        const result = await this.sendRequest('tools/call', { name, arguments: args })
        const normalized = normalizeMcpToolCallResult(result)
        return {
            content: normalized.content,
            structuredContent: normalized.structuredContent,
            isError: result.isError === true,
        }
    }

    private async ensureInitialized(): Promise<void> {
        if (!this.initializePromise) {
            this.initializePromise = this.initialize().catch((error) => {
                this.initializePromise = null
                throw error
            })
        }
        return this.initializePromise
    }

    private async initialize(): Promise<void> {
        const result = await this.sendRequest('initialize', {
            protocolVersion: REMOTE_MCP_PROTOCOL_VERSION,
            capabilities: {},
            clientInfo: {
                name: 'SecureBlob Agent',
                version: '0.1.0',
            },
        })

        if (!result || typeof result !== 'object') {
            throw new Error(`MCP server "${this.serverName}" returned an invalid initialize result`)
        }

        await this.sendNotification('notifications/initialized', {})
    }

    private async sendRequest(
        method: string,
        params?: Record<string, unknown>,
        allowSessionRecovery = true,
    ): Promise<Record<string, unknown>> {
        const callId = `mcp-${this.nextId++}`
        const startTime = Date.now()
        const capturedAt = new Date().toISOString()

        let isError = false
        let redactedResult: string | null = null
        let resultData: Record<string, unknown> | null = null

        const redactedParams = this.formatParamsPreview(params)
        const connectionLabel = this.serverName

        try {
            const body: JsonRpcRequest = {
                jsonrpc: '2.0',
                id: callId,
                method,
                ...(params ? { params } : {}),
            }

            const response = await this.fetchRequest(this.url, {
                method: 'POST',
                headers: this.buildHeaders(),
                body: JSON.stringify(body),
            })
            await this.runHttpResponseHook(response, 'request')

            const sessionId = response.headers.get('MCP-Session-Id')
            if (sessionId) this.sessionId = sessionId

            if (!response.ok) {
                const errorText = await response.text().catch(() => '')
                if (this.shouldRecoverStaleSession(method, response.status, errorText, allowSessionRecovery)) {
                    // MCP sessions are ephemeral across expiry/restart; recover once with a fresh handshake.
                    await this.reinitializeForRecoveredSession()
                    return this.sendRequest(method, params, false)
                }

                isError = true
                redactedResult = `${response.status} ${errorText || response.statusText}`.trim()
                emitSdkEvent(this.hooks.observers, {
                    domain: 'mcp.http',
                    level: 'error',
                    message: 'Remote MCP HTTP request failed.',
                    data: {
                        url: this.url,
                        method: 'POST',
                        status: response.status,
                        requestKind: 'request',
                        requestMethod: method,
                        connectionLabel,
                    },
                })
                throw new Error(`MCP ${method} failed for "${this.serverName}": ${response.status} ${errorText || response.statusText}`.trim())
            }

            const message = await this.readJsonRpcResponse(response, callId)
            if (!('error' in message)) {
                resultData = message.result as Record<string, unknown>
            }

            if ('error' in message) {
                isError = true
                redactedResult = message.error.message ?? String(message.error)
                throw new Error(`MCP ${method} failed for "${this.serverName}": ${message.error.message}`)
            }

            if (resultData === null) {
                throw new Error(`MCP server "${this.serverName}" returned no result for request ${callId}`)
            }

            redactedResult = this.formatResultPreview(resultData)
            return resultData
        } finally {
            const durationMs = Date.now() - startTime
            const observation: RemoteMcpRequestObservation = {
                method,
                params,
                connectionLabel,
                connectionType: 'remote',
                callId,
                capturedAt,
                durationMs,
                isError,
                redactedParams,
                redactedResult,
                resultData,
                toolResult: method === 'tools/call' && resultData !== null
                    ? normalizeMcpToolCallResult(resultData)
                    : null,
            }
            emitSdkEvent(this.hooks.observers, {
                domain: 'mcp',
                level: isError ? 'error' : 'info',
                message: 'Remote MCP request completed.',
                data: {
                    connectionLabel,
                    method,
                    callId,
                    capturedAt,
                    durationMs,
                    isError,
                    redactedParams,
                    redactedResult,
                },
            })
            emitSdkMetric(this.hooks.observers, 'sdk.mcp.request.duration_ms', durationMs, {
                method,
                success: isError ? 'false' : 'true',
            })
            await this.runRequestCompleteHook(observation)
        }
    }

    private shouldRecoverStaleSession(
        method: string,
        status: number,
        errorText: string,
        allowSessionRecovery: boolean,
    ): boolean {
        return allowSessionRecovery
            && this.sessionId !== null
            && status === 404
            && method !== 'initialize'
            && method !== 'notifications/initialized'
            && /Unknown MCP session/i.test(errorText)
    }

    private async reinitializeForRecoveredSession(): Promise<void> {
        this.sessionId = null
        this.initializePromise = null
        await this.ensureInitialized()
    }

    private formatParamsPreview(params?: Record<string, unknown>): string | null {
        if (!params) return null

        if (params && typeof params === 'object' && 'name' in params && 'arguments' in params) {
            const name = typeof params.name === 'string' ? params.name : String(params.name)
            return `{name="${name}"}`
        }

        try {
            const serialized = JSON.stringify(params)
            if (serialized.length <= 200) return serialized
            return serialized.slice(0, 200) + '...'
        } catch {
            return null
        }
    }

    private formatResultPreview(result: unknown): string | null {
        if (result === null || result === undefined) return null

        try {
            const serialized = JSON.stringify(result)
            if (serialized.length <= 200) return serialized
            return serialized.slice(0, 200) + '...'
        } catch {
            return null
        }
    }

    private async sendNotification(method: string, params?: Record<string, unknown>): Promise<void> {
        const body: JsonRpcNotification = {
            jsonrpc: '2.0',
            method,
            ...(params ? { params } : {}),
        }

        const response = await this.fetchRequest(this.url, {
            method: 'POST',
            headers: this.buildHeaders(),
            body: JSON.stringify(body),
        })
        await this.runHttpResponseHook(response, 'notification')

        const sessionId = response.headers.get('MCP-Session-Id')
        if (sessionId) this.sessionId = sessionId

        if (!response.ok && response.status !== 202) {
            const errorText = await response.text().catch(() => '')
            throw new Error(`MCP ${method} failed for "${this.serverName}": ${response.status} ${errorText || response.statusText}`.trim())
        }
    }

    private buildHeaders(): HeadersInit {
        const headers: Record<string, string> = {
            Accept: 'application/json, text/event-stream',
            'Content-Type': 'application/json',
            'MCP-Protocol-Version': REMOTE_MCP_PROTOCOL_VERSION,
            ...(this.headers ?? {}),
        }
        if (this.sessionId) {
            headers['MCP-Session-Id'] = this.sessionId
        }
        return headers
    }

    private async readJsonRpcResponse(response: Response, requestId: string | number): Promise<JsonRpcMessage> {
        const contentType = response.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
            const json = await response.json()
            const message = findJsonRpcMessage(json, requestId)
            if (!message) throw new Error(`MCP server "${this.serverName}" returned no response for request ${requestId}`)
            return message
        }
        if (contentType.includes('text/event-stream')) {
            if (!response.body) throw new Error(`MCP server "${this.serverName}" returned an empty SSE body`)
            return readSseJsonRpc(response.body, requestId, this.serverName)
        }
        const text = await response.text().catch(() => '')
        throw new Error(`Unsupported MCP response type from "${this.serverName}": ${contentType || 'unknown'} ${text}`.trim())
    }

    private async runHttpResponseHook(response: Response, requestKind: 'request' | 'notification'): Promise<void> {
        const hook = this.hooks.onHttpResponse
        if (!hook) return

        try {
            await hook({
                url: this.url,
                method: 'POST',
                requestKind,
                response,
            })
        } catch (error) {
            sdkConsole.warn(`[RemoteMcpClient] onHttpResponse hook failed for "${this.serverName}"`, error)
        }
    }

    private async runRequestCompleteHook(observation: RemoteMcpRequestObservation): Promise<void> {
        const hook = this.hooks.onRequestComplete
        if (!hook) return

        try {
            await hook(observation)
        } catch (error) {
            sdkConsole.warn(`[RemoteMcpClient] onRequestComplete hook failed for "${this.serverName}"`, error)
        }
    }
}

function findJsonRpcMessage(payload: unknown, requestId: string | number): JsonRpcMessage | null {
    const messages = Array.isArray(payload) ? payload : [payload]
    for (const message of messages) {
        if (!message || typeof message !== 'object') continue
        const candidate = message as Partial<JsonRpcMessage>
        if (candidate.id === requestId && (('result' in candidate) || ('error' in candidate))) {
            return candidate as JsonRpcMessage
        }
    }
    return null
}

async function readSseJsonRpc(
    stream: ReadableStream<Uint8Array>,
    requestId: string | number,
    serverName: string,
): Promise<JsonRpcMessage> {
    const reader = stream.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
        const { value, done } = await reader.read()
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done })

        let boundary = findEventBoundary(buffer)
        while (boundary >= 0) {
            const rawEvent = buffer.slice(0, boundary)
            buffer = buffer.slice(boundary + eventBoundaryLength(buffer, boundary))
            const payload = parseSseEvent(rawEvent)
            if (payload !== null) {
                const message = findJsonRpcMessage(payload, requestId)
                if (message) return message
            }
            boundary = findEventBoundary(buffer)
        }

        if (done) break
    }

    throw new Error(`MCP server "${serverName}" closed the SSE stream before replying to ${requestId}`)
}

function parseSseEvent(rawEvent: string): unknown | null {
    const dataLines: string[] = []
    for (const line of rawEvent.replace(/\r/g, '').split('\n')) {
        if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart())
    }
    if (dataLines.length === 0) return null
    try {
        return JSON.parse(dataLines.join('\n'))
    } catch {
        return null
    }
}

function findEventBoundary(buffer: string): number {
    const unix = buffer.indexOf('\n\n')
    const windows = buffer.indexOf('\r\n\r\n')
    if (unix === -1) return windows
    if (windows === -1) return unix
    return Math.min(unix, windows)
}

function eventBoundaryLength(buffer: string, index: number): number {
    return buffer.startsWith('\r\n\r\n', index) ? 4 : 2
}
